import ExtendedDrawerCurrentProfileAction from '@/components/Drawer/ExtendedDrawerCurrentProfileAction'
import { extendedDrawerWidth } from '@/components/Drawer/styles'
import { ColorEnum } from '@/constants/color'
import { getFocusedRouteResetPath } from '@/features/navigation/drawer/getFocusedRouteResetPath'
import { RenderedDrawerRoute } from '@/features/navigation/drawer/types'
import { MenuItem } from '@/features/ui/navigation'
import { useAppSelector } from '@/hooks/redux'
import { authSelectors } from '@/redux/reducers/auth/selectors'
import { DrawerContentComponentProps, DrawerItem } from '@react-navigation/drawer'
import { CommonActions } from '@react-navigation/native'
import { usePathname, useRouter } from 'expo-router'
import { FC, useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

const ExtendedDrawerContent: FC<
    DrawerContentComponentProps & { renderedRoutes: RenderedDrawerRoute[] }
> = ({ state, navigation, renderedRoutes }) => {
    const { styles } = useStyles(stylesheet)
    const profile = useAppSelector(authSelectors.currentProfile)
    const pathname = usePathname()
    const router = useRouter()

    const isFocused = useCallback(
        (name: string) => state.routeNames[state.index] === name,
        [state.index, state.routeNames]
    )

    const { topRoutes, bottomRoutes } = useMemo(
        () => ({
            topRoutes: renderedRoutes.filter((r) => (r.section ?? 'top') === 'top'),
            bottomRoutes: renderedRoutes.filter((r) => (r.section ?? 'top') === 'bottom'),
        }),
        [renderedRoutes]
    )

    const renderRoute = (route: RenderedDrawerRoute) => {
        const focused = isFocused(route.name)

        const handlePress = () => {
            if (focused) {
                const resetPath = getFocusedRouteResetPath(route, pathname, focused)

                if (resetPath) router.dismissTo(resetPath)

                return
            }

            navigation.dispatch({
                ...CommonActions.navigate(route.name),
                target: state.key,
            })
        }

        return (
            <DrawerItem
                key={route.name}
                focused={focused}
                activeBackgroundColor={ColorEnum.Transparent}
                activeTintColor={ColorEnum.BRICK_500}
                inactiveTintColor={ColorEnum.WHITE}
                label={({ focused }) => (
                    <MenuItem
                        Icon={route.Icon}
                        label={route.title}
                        active={focused}
                        Badge={route.Badge}
                        style={styles.drawerItem}
                    />
                )}
                onPress={handlePress}
                style={{ overflow: 'visible' }}
            />
        )
    }

    return (
        <View style={styles.container}>
            {topRoutes.map(renderRoute)}
            <View style={styles.spacer} />
            {profile && <ExtendedDrawerCurrentProfileAction profileName={profile.name} />}
            {bottomRoutes.map(renderRoute)}
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    container: {
        flex: 1,
        backgroundColor: ColorEnum.JET_900,
        paddingTop: 16,
        paddingBottom: 16 + insets.bottom,
        gap: 24,
    },
    spacer: {
        flex: 1,
    },
    drawerItem: {
        width: extendedDrawerWidth,
        marginVertical: -15,
        marginLeft: -16,
    },
}))

export default ExtendedDrawerContent
